import React, { createContext, useContext, useState, ReactNode } from "react";

import { countryTranslations, getDisplayCountryName } from "../utils/country";

type Language = "en" | "sv";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  tCountry: (countryName: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Menu
    "menu.favorites": "My Favorites",
    "menu.history": "History Log",
    "menu.customize_feed": "Customize Home Feed",
    "menu.discover": "Discover Mode",
    "menu.zen": "Zen Mode",
    "menu.theme.dark": "Dark",
    "menu.theme.light": "Light",
    "menu.guest": "Guest Listener",
    "menu.free": "Free Tier",

    // Headers
    "header.featured": "Featured",
    "header.featured.sub": "TOP PICKS",
    "header.documentary": "Society & Culture",
    "header.documentary.sub": "POLITICS, HISTORY & SOCIETY",
    "header.lifestyle": "Comedy & Lifestyle",
    "header.lifestyle.sub": "DAILY LIFE, HUMOR & FUN",
    "header.health": "Health & Science",
    "header.health.sub": "MEDICINE, RESEARCH & FITNESS",
    "header.fishing": "Fishing & Hunting",
    "header.fishing.sub": "TIPS & STORIES",

    // Home Sections
    "sidebar.favorites": "Your Favorites",
    "home.your_tuned_stations": "YOUR TUNED STATIONS",
    "home.most_played": "Most Played on StreamEater",
    "home.most_played_in": "Most Played in",
    "home.last_7_days": "Last 7 Days",
    "home.recently_played": "Recently Played",
    "home.latest_stations_added": "LATEST STATIONS ADDED",
    "home.stations_count": "station",
    "home.stations_count_plural": "stations",
    "home.most_played_podcasts": "Most Played on StreamEater – Podcasts",

    // FilterBar
    "filter.start": "Start",
    "filter.playing": "playing",
    "filter.from": "from",
    "filter.search_placeholder":
      "Smart Search (e.g. 'Pop', 'Sweden', 'Pod')...",
    "filter.find_country": "Find country...",
    "filter.worldwide": "Worldwide",
    "filter.near_me": "Near Me",

    "profile.home_view_settings": "Home View Settings",
    "profile.home_view_desc":
      "Toggle which sections you want to see on your home page.",
    "profile.no_customizable_sections": "No customizable sections found.",
    "filter.no_countries": "No countries found.",
    "filter.try_radio": "Try switching to Radio.",
    "filter.top_in": "Top in",
    "filter.category": "Category",
    "filter.all_categories": "All Categories",
    "filter.all_countries": "All Countries",
    "filter.country": "Country",
    "filter.sort": "Sort",
    "filter.filter": "Filter",
    "filter.settings": "Settings",
    "filter.apply": "Apply",
    "filter.customize_feed": "Customize Feed",
    "filter.layout": "Layout",
    "filter.reset": "Reset",
    "filter.toggle_sections": "Toggle sections to customize your feed",

    // Front Door
    "front.headline.1": "Stop searching.",
    "front.headline.2": "Start listening.",
    "front.subhead":
      "Radio and podcasts in one place. Your favorites sync everywhere.",
    "front.cta.podcasts": "Explore Podcasts",
    "front.cta.radio": "Start Radio",
    "front.label.local": "Local favorites",
    "front.label.worldwide": "+ worldwide stations",
    "front.editorial.kicker": "Weekly Picks",
    "front.editorial.title": "Streameater Recommends",
    "front.editorial.subtitle":
      "This week's handpicked mix for steady listening.",
    "front.story.station.kicker": "Live Radio",
    "front.story.station.headline": "Your station, not a playlist.",
    "front.story.station.body":
      "Real stations from Sweden and beyond – curated by people, not algorithms.",
    "front.story.sync.kicker": "Always in Sync",
    "front.story.sync.headline": "Continue where you left off.",
    "front.story.sync.body":
      "Your favorites and listening history follow you across all your devices.",
    // Quick Listen section
    "front.quickstart.kicker": "Easy Start",
    "front.quickstart.title": "Start listening in seconds",
    "front.quickstart.step1": "Pick Radio or Podcasts",
    "front.quickstart.step2": "Browse, search, or get surprised",
    "front.quickstart.step3": "Hit play – done!",
    "front.quickstart.cta": "Start now",
    // Popular section
    "front.popular.kicker": "Top Charts",
    "front.popular.title": "Popular right now",
    "front.popular.type.radio": "Radio",
    "front.popular.type.podcast": "Podcast",

    // HomeView
    "home.connected_to": "Connected to",
    "home.no_stations": "No Stations Found",
    "home.try_different": "Try selecting a different category or country",
    "home.let_us_choose": "Let Stream Eater Choose",
    "home.tell_us": "Tell Stream Eater your current mood or click Surprise Me.",
    "home.surprise_me": "Surprise Me",
    "home.surprise_again": "Surprise Me Again",
    "home.scanning": "Scanning...",

    // General
    "common.select_all": "Select All",
    "common.deselect_all": "Deselect All",
    "common.section_hidden": "Section hidden",
    "common.undo": "UNDO",
    "common.view_all": "View All",
    "daily_fresh.title.radio": "Daily Drops",
    "daily_fresh.subtitle.radio": "New mix in:",
    "daily_fresh.title.pods": "Daily Pod Drops",
    "daily_fresh.subtitle.pods": "New mix in:",
    "daily_fresh.fresh": "FRESH",
    viewAll: "View All",
    loading: "Loading...",
    error: "Error",
    no_signal: "NO SIGNAL DETECTED",
    scanning_range: "Scanning frequency range",
    no_broadcasts: "No active broadcasts found in this sector.",
    static_noise:
      "Static noise on all frequencies. Try adjusting your filters.",
    reset_frequency: "Reset Frequency",
    // Footer
    footer_download_ios_label: "Download from",
    footer_download_android_label: "Get it on",
    footer_store_appstore: "App Store",
    footer_store_googleplay: "Google Play",
    footer_store_coming_soon: "Coming soon",
    footer_link_terms: "Terms of Use",
    footer_link_privacy: "Privacy Policy",
    footer_link_cookies: "Cookies",
    footer_link_about: "About Streameater",
    footer_link_help_center: "Help Center",
    footer_cta_ready: "Ready to listen?",
    footer_cta_explore_radio: "Explore Radio",
    footer_cta_find_podcasts: "Find Podcasts",

    // Legal
    legal_back_to_home: "Back to Home",
    legal_terms_title: "Terms of Use",
    legal_privacy_title: "Privacy Policy",
    legal_cookies_title: "Cookie Policy",
    legal_about_title: "About Streameater",
    legal_help_title: "Help Center",
    legal_not_found_title: "Page Not Found",
    legal_not_found_body: "The requested page could not be found.",

    // Search
    "search.results_for": "Results for",
    "search.stations_in": "Stations in",
    "search.search_results": "Search Results",
    "search.no_stations": "No stations found. Try a different search.",
    "search.sort": "Sort",
    "search.sort_relevance": "Relevance",
    "search.sort_name": "Name (A-Z)",
    "search.sort_popular": "Most Popular",
    "search.sort_bitrate": "High Quality",
    "search.sort_country": "Country",
    "search.type_all": "All",
    "search.type_radio": "Radio",
    "search.type_podcasts": "Podcasts",

    // Development Status
    dev_status_title: "Platform in Development",
    dev_status_message:
      "Streameater is currently in active development. Thank you for your patience as we continue to improve the platform. We appreciate early listeners and welcome your feedback!",
    dev_email_feedback: "Questions or ideas? Email us at",
    dev_beta_title:
      "Streameater is in beta — tell us what works and what needs polish.",
    dev_beta_body:
      "We’re still polishing the experience; expect changes and occasional hiccups as we iterate.",
    dev_beta_feedback: "Questions or ideas? Email us at",

    // Login View
    "login.welcome": "Welcome Back",
    "login.subtitle": "Sync favorites, history and subscriptions.",
    "login.google": "Continue with Google",
    "login.or_email": "or with email",
    "login.email_placeholder": "Email",
    "login.password_placeholder": "Password",
    "login.sign_in": "Sign In",
    "login.no_account": "Don't have an account?",
    "login.create_account": "Create account for free",
    "login.email_label": "Email",
    "login.password_label": "Password",
    "login.join_title": "Join StreamEater",
    "login.get_most_out": "Get the most out of StreamEater",
    "login.save_favorites_trigger": "Save your favorites for later!",
    "login.track_history_trigger": "Track your listening history!",
    "login.create_playlists_trigger": "Create and share your own playlists!",
    "login.with_free_account": "With a free account you get",
    "login.benefit_favorites": "Save favorites across devices",
    "login.benefit_history": "Sync your listening history",
    "login.benefit_recommendations": "Personalized recommendations",
    "login.terms": "By continuing, you agree to our Terms & Privacy Policy.",
    "login.continue_without_account": "Continue without an account",
    "login.create_account_anytime": "You can create an account anytime",

    // Player Context
    "player.context.carousel": "Carousel",
    "player.context.grid": "Grid",
    "player.context.search": "Search",
    "player.context.favorites": "Favorites",
    "player.context.related": "Related",
    "player.context.history": "History",
    "player.context.of": "of",

    // Header
    "header.radio": "Radio",
    "header.podcasts": "Podcasts",
    "header.pods": "Pods",
    "header.worldwide_tuner": "Worldwide Tuner",
    "header.toaster": "Toaster",

    // Quick Actions
    "quickActions.surpriseMe": "Surprise Me!",
    "quickActions.favorites": "Favorites",
    "quickActions.history": "Recently Played",
    "quickActions.home": "Home",

    // RadioView
    "radio.live": "Live",
    "radio.related_channels": "Related Channels",
    "radio.show_all": "Show all",
    "radio.show_less": "Show Less",
    "radio.read_more": "Read More",
    "radio.no_related": "No related channels found.",

    // PodcastView
    "podcast.episodes": "Episodes",
    "podcast.continue_listening": "Continue Listening",
    "podcast.newest": "Newest",
    "podcast.oldest": "Oldest",
    "podcast.no_episodes": "No episodes found for this podcast.",
    "podcast.no_description": "No description available.",
    "podcast.ratings": "Ratings",
    "podcast.rate": "Rate",
    "podcast.rate_episode": "Rate this episode",
    "podcast.edit_review": "Edit your review",
    "filter.customize_home": "Customize Home",

    // HeroCarousel
    "carousel.curated_for_you": "Curated for you",
    "carousel.streameater_recommends": "Streameater Recommends",
    "home.streameater_recommends": "Streameater Recommends",
    "carousel.play_now": "Play Now",
    "carousel.playing": "Playing",

    // Sidebar (DJ Eater)
    "sidebar.dj_eater": "DJ Eater",
    "sidebar.ai_broadcast_unit": "AI Broadcast Unit",
    "sidebar.connect": "Connect",
    "sidebar.disconnect": "Disconnect",
    "sidebar.offline": "Offline",
    "sidebar.live_feed": "Live Feed",
    "sidebar.auto_announce": "Auto-Announce",
    "sidebar.sleep_timer": "Sleep Timer",
    "sidebar.upbeat_mode": "Upbeat Mode",
    "sidebar.chill_mode": "Chill Mode",
    "sidebar.intel_mode": "Intel Mode",
    "sidebar.channel_open": "Channel Open",
    "sidebar.ready_for_requests": "Ready for requests...",
    "sidebar.surprise_me": "Surprise Me",
    "sidebar.relaxing": "Relaxing",
    "sidebar.type_request": "Type your request...",

    // LandingPage
    "landing.curated_for_you": "Curated for you",
    "landing.hero_headline_1": "Stop searching.",
    "landing.hero_headline_2": "Start listening.",
    "landing.hero_description":
      "Radio, podcasts, history – all in one place, personalized for you. Sync across devices and pick up where you left off.",
    "landing.personalized_for_you": "personalized for you",
    "landing.start_radio": "Start Radio",
    "landing.explore_podcasts": "Explore Podcasts",
    "landing.sign_in_full_experience": "Sign in for the full experience",
    "landing.why_streameater": "Why Streameater?",
    "landing.features": "Features",
    "landing.all_in_one": "All in one place",
    "landing.all_in_one_desc":
      "Radio and podcasts in the same app. Stop switching between apps.",
    "landing.sync_everything": "Sync everything",
    "landing.sync_everything_desc":
      "Favorites and history follow you across all your devices.",
    "landing.stable_reliable": "Stable & reliable",
    "landing.stable_reliable_desc":
      "Robust streams with automatic reconnection.",
    "landing.curated_content": "Curated content",
    "landing.curated_content_desc":
      "Hand-picked playlists and mixes based on your interests.",
    "landing.smart_recommendations": "Smart recommendations coming soon",
    "landing.building_special": "We're building something special",
    "landing.get_started": "Get started in 30 seconds",
    "landing.quickstart": "Quickstart",
    "landing.step_1": "Choose Radio or Podcasts",
    "landing.step_2": "Browse or search for what you like",
    "landing.step_3": "Press play – done!",
    "landing.get_started_btn": "Get started",
    "landing.sign_in": "Sign in",
    "landing.popular_now": "Popular right now",
    "landing.top_charts": "Top Charts",
    "landing.local_favorites": "Local favorites",
    "landing.worldwide_stations": "+ worldwide stations",
    "landing.play": "Play",

    // FrontDoorFeatures
    "frontdoor.features.kicker": "Why Streameater",
    "frontdoor.features.headline": "One place for radio & podcasts.",
    "frontdoor.features.body":
      "Switch seamlessly between live radio and your favorite podcasts. All your listening history and favorites in one unified experience.",

    // Player
    "player.share": "Share",
    "player.share_message_radio": "I'm listening to {name} live on StreamEater. Listen here:",
    "player.share_message_podcast": "I'm listening to {name} on StreamEater. Listen here:",
    "player.link_copied": "Link copied!",

    // Filter
    "filter.show_all": "Show All...",
    "filter.show_less": "Show Less",
    "filter.search_country": "Search country...",
  },
  sv: {
    // Menu
    "menu.favorites": "Mina Favoriter",
    "menu.history": "Historik",
    "menu.discover": "Upptäck",
    "menu.zen": "Zen-läge",
    "menu.theme.dark": "Mörkt",
    "menu.theme.light": "Ljust",
    "menu.guest": "Gästlyssnare",
    "menu.free": "Gratisnivå",

    // Headers
    "header.featured": "Utvalda",
    "header.featured.sub": "TOPPVAL FRÅN PODDTOPPEN",
    "header.documentary": "Samhälle & Dokumentär",
    "header.documentary.sub": "POLITIK, HISTORIA & SAMHÄLLE",
    "header.lifestyle": "Humor & Livsstil",
    "header.lifestyle.sub": "VARDAG, HUMOR & NÖJE",
    "header.health": "Hälsa & Vetenskap",
    "header.health.sub": "MEDICIN, FORSKNING & TRÄNING",
    "header.fishing": "Fiske & Jakt",
    "header.fishing.sub": "SVENSKA FISKETIPS & JAKTHISTORIER",

    // Home Sections
    "sidebar.favorites": "Dina Favoriter",
    "menu.customize_feed": "Anpassa ditt flöde",
    "home.your_tuned_stations": "DINA INSTÄLLDA STATIONER",
    "home.most_played": "Mest Spelade på StreamEater",
    "home.most_played_in": "Mest Spelade i",
    "home.last_7_days": "Senaste 7 Dagarna",
    "home.recently_played": "Nyligen Spelade",
    "home.latest_stations_added": "SENAST TILLAGDA STATIONER",
    "home.stations_count": "station",
    "home.stations_count_plural": "stationer",
    "home.most_played_podcasts": "Mest Spelade på StreamEater – Podcasts",

    // FilterBar
    "filter.start": "Start",
    "filter.playing": "spelar",
    "filter.from": "från",
    "filter.search_placeholder":
      "Smart Sök (t.ex. 'Pop', 'Sverige', 'Podd')...",
    "filter.find_country": "Hitta land...",
    "filter.worldwide": "Hela Världen",
    "filter.near_me": "Nära Mig",
    "filter.other_regions": "Andra Regioner",
    "filter.no_countries": "Inga länder hittades.",
    "filter.try_radio": "Testa att byta till Radio.",
    "filter.top_in": "Topp i",
    "filter.category": "Kategori",
    "filter.all_categories": "Alla Kategorier",
    "filter.all_countries": "Alla Länder",
    "filter.country": "Land",
    "filter.sort": "Sortering",
    "filter.filter": "Filtrera",
    "filter.settings": "Inställningar",
    "filter.apply": "Tillämpa",
    "filter.customize_feed": "Anpassa Flöde",
    "filter.layout": "Layout",
    "filter.reset": "Återställ",
    "filter.toggle_sections": "Växla sektioner för att anpassa ditt flöde",

    // Front Door
    "front.headline.1": "Sluta leta.",
    "front.headline.2": "Börja lyssna.",
    "front.subhead":
      "Radio och poddar på ett ställe. Dina favoriter synkas överallt.",
    "front.cta.podcasts": "Utforska Poddar",
    "front.cta.radio": "Starta Radio",
    "front.label.local": "Lokala favoriter",
    "front.label.worldwide": "+ globala stationer",
    "front.editorial.kicker": "Veckans Tips",
    "front.editorial.title": "Streameater Rekommenderar",
    "front.editorial.subtitle":
      "Veckans handplockade mix för perfekt lyssning.",
    "front.story.station.kicker": "Live Radio",
    "front.story.station.headline": "Din station, inte en spellista.",
    "front.story.station.body":
      "Riktiga stationer från Sverige och världen – kurerade av människor, inte algoritmer.",
    "front.story.sync.kicker": "Alltid Synkat",
    "front.story.sync.headline": "Fortsätt där du slutade.",
    "front.story.sync.body":
      "Dina favoriter och lyssningshistorik följer med dig till alla dina enheter.",
    // Quick Listen section
    "front.quickstart.kicker": "Kom igång",
    "front.quickstart.title": "Börja lyssna på sekunder",
    "front.quickstart.step1": "Välj Radio eller Poddar",
    "front.quickstart.step2": "Bläddra, sök eller bli överraskad",
    "front.quickstart.step3": "Tryck play – klart!",
    "front.quickstart.cta": "Starta nu",
    // Popular section
    "front.popular.kicker": "Topplistan",
    "front.popular.title": "Populärt just nu",
    "front.popular.type.radio": "Radio",
    "front.popular.type.podcast": "Podd",
    "home.connected_to": "Ansluten till",
    "home.no_stations": "Inga Stationer Hittades",
    "home.try_different": "Försök välja en annan kategori eller land",
    "home.let_us_choose": "Låt Stream Eater Välja",
    "home.tell_us":
      "Berätta din nuvarande känsla eller klicka på Överraska Mig.",
    "home.surprise_me": "Överraska Mig",
    "home.surprise_again": "Överraska Mig Igen",
    "home.scanning": "Skannar...",

    // General
    "common.select_all": "Markera alla",
    "common.deselect_all": "Avmarkera alla",
    "common.section_hidden": "Sektion dold",
    "common.undo": "ÅNGRA",
    "common.view_all": "Visa alla",
    "daily_fresh.title.radio": "Dagens Drops",
    "daily_fresh.subtitle.radio": "Ny mix om:",
    "daily_fresh.title.pods": "Dagens Podd-Drops",
    "daily_fresh.subtitle.pods": "Ny mix om:",
    "daily_fresh.fresh": "FÄRSKT",
    viewAll: "Visa mer",
    loading: "Laddar...",
    error: "Fel",
    no_signal: "INGEN SIGNAL UPPTÄCKTES",
    scanning_range: "Skannar frekvensområde",
    no_broadcasts: "Inga aktiva sändningar hittades i denna sektor.",
    static_noise:
      "Statiskt brus på alla frekvenser. Prova att justera dina filter.",
    reset_frequency: "Återställ Frekvens",
    // Footer
    footer_download_ios_label: "Ladda ner från",
    footer_download_android_label: "Ladda ner på",
    footer_store_appstore: "App Store",
    footer_store_googleplay: "Google Play",
    footer_store_coming_soon: "Kommer snart",
    footer_link_terms: "Användarvillkor",
    footer_link_privacy: "Integritetspolicy",
    footer_link_cookies: "Cookies",
    footer_link_about: "Om Streameater",
    footer_link_help_center: "Hjälpcenter",
    footer_cta_ready: "Redo att lyssna?",
    footer_cta_explore_radio: "Utforska Radio",
    footer_cta_find_podcasts: "Hitta Podcasts",

    // Legal
    legal_back_to_home: "Tillbaka till startsidan",
    legal_terms_title: "Användarvillkor",
    legal_privacy_title: "Integritetspolicy",
    legal_cookies_title: "Cookiepolicy",
    legal_about_title: "Om Streameater",
    legal_help_title: "Hjälpcenter",
    legal_not_found_title: "Sidan hittades inte",
    legal_not_found_body: "Den begärda sidan kunde inte hittas.",

    // Search
    "search.results_for": "Resultat för",
    "search.stations_in": "Stationer i",
    "search.search_results": "Sökresultat",
    "search.no_stations": "Inga stationer hittades. Prova en annan sökning.",
    "search.sort": "Sortera",
    "search.sort_relevance": "Relevans",
    "search.sort_name": "Namn (A-Ö)",
    "search.sort_popular": "Mest populära",
    "search.sort_bitrate": "Hög kvalitet",
    "search.sort_country": "Land",
    "search.type_all": "Alla",
    "search.type_radio": "Radio",
    "search.type_podcasts": "Podcasts",

    // Development Status
    dev_status_title: "Plattform under utveckling",
    dev_status_message:
      "Streameater är för närvarande under aktiv utveckling. Tack för ditt tålamod medan vi fortsätter att förbättra plattformen. Vi uppskattar våra tidiga lyssnare och välkomnar din feedback!",
    dev_email_feedback: "Har du feedback eller förslag? Maila oss på",
    dev_beta_title:
      "Streameater är i beta – berätta gärna vad som funkar och vad som skaver.",
    dev_beta_body:
      "Vi fortsätter slipa upplevelsen; räkna med förändringar och småglapp medan vi itererar.",
    dev_beta_feedback: "Har du feedback eller förslag? Maila oss på",

    // Login View
    "login.welcome": "Välkommen Tillbaka",
    "login.subtitle": "Synka dina favoriter, historik och prenumerationer.",
    "login.google": "Logga in med Google",
    "login.or_email": "eller med e-post",
    "login.email_placeholder": "E-post",
    "login.password_placeholder": "Lösenord",
    "login.sign_in": "Logga In",
    "login.no_account": "Har du inget konto?",
    "login.create_account": "Skapa konto kostnadsfritt",
    "login.email_label": "E-post",
    "login.password_label": "Lösenord",
    "login.join_title": "Gå med i StreamEater",
    "login.get_most_out": "Få ut det mesta av StreamEater",
    "login.save_favorites_trigger": "Spara dina favoriter för senare!",
    "login.track_history_trigger": "Följ din lyssningshistorik!",
    "login.create_playlists_trigger": "Skapa och dela dina egna spellistor!",
    "login.with_free_account": "Med ett gratis konto får du",
    "login.benefit_favorites": "Spara favoriter på alla enheter",
    "login.benefit_history": "Synka din lyssningshistorik",
    "login.benefit_recommendations": "Personliga rekommendationer",
    "login.terms": "Genom att fortsätta godkänner du våra villkor och integritetspolicy.",
    "login.continue_without_account": "Fortsätt utan konto",
    "login.create_account_anytime": "Du kan skapa ett konto när som helst",

    // Player Context
    "player.context.carousel": "Karusellen",
    "player.context.grid": "Rutnät",
    "player.context.search": "Sökresultat",
    "player.context.favorites": "Favoriter",
    "player.context.related": "Relaterade",
    "player.context.history": "Historik",
    "player.context.of": "av",

    // Header
    "header.radio": "Radio",
    "header.podcasts": "Poddar",
    "header.pods": "Poddar",
    "header.worldwide_tuner": "Världsradio",
    "header.toaster": "Toaster",

    // Quick Actions
    "quickActions.surpriseMe": "Överraska Mig!",
    "quickActions.favorites": "Favoriter",
    "quickActions.history": "Nyligen Spelade",
    "quickActions.home": "Hem",

    // RadioView
    "radio.live": "Live",
    "radio.related_channels": "Relaterade Kanaler",
    "radio.show_all": "Visa alla",
    "radio.show_less": "Visa mindre",
    "radio.read_more": "Läs mer",
    "radio.no_related": "Inga relaterade kanaler hittades.",

    // PodcastView
    "podcast.episodes": "Avsnitt",
    "podcast.continue_listening": "Fortsätt Lyssna",
    "podcast.newest": "Nyaste",
    "podcast.oldest": "Äldsta",
    "podcast.no_episodes": "Inga avsnitt hittades för denna podd.",
    "podcast.no_description": "Ingen beskrivning tillgänglig.",
    "podcast.ratings": "Betyg",
    "podcast.rate": "Betygsätt",
    "podcast.rate_episode": "Betygsätt detta avsnitt",
    "podcast.edit_review": "Redigera ditt omdöme",
    "filter.customize_home": "Anpassa startsidan",

    // HeroCarousel
    "carousel.curated_for_you": "Utvalt för dig",
    "carousel.streameater_recommends": "Streameater Rekommenderar",
    "home.streameater_recommends": "Streameater Rekommenderar",
    "carousel.play_now": "Spela Nu",
    "carousel.playing": "Spelar",

    // Sidebar (DJ Eater)
    "sidebar.dj_eater": "DJ Eater",
    "sidebar.ai_broadcast_unit": "AI Sändningsenhet",
    "sidebar.connect": "Anslut",
    "sidebar.disconnect": "Koppla från",
    "sidebar.offline": "Offline",
    "sidebar.live_feed": "Live-flöde",
    "sidebar.auto_announce": "Auto-annonsering",
    "sidebar.sleep_timer": "Sovtimer",
    "sidebar.upbeat_mode": "Uppåt-läge",
    "sidebar.chill_mode": "Lugnt läge",
    "sidebar.intel_mode": "Info-läge",
    "sidebar.channel_open": "Kanal öppen",
    "sidebar.ready_for_requests": "Redo för förfrågningar...",
    "sidebar.surprise_me": "Överraska Mig",
    "sidebar.relaxing": "Avslappnande",
    "sidebar.type_request": "Skriv din förfrågan...",

    // LandingPage
    "landing.curated_for_you": "Utvalt för dig",
    "landing.hero_headline_1": "Sluta leta.",
    "landing.hero_headline_2": "Börja lyssna.",
    "landing.hero_description":
      "Radio, poddar, historik – allt på ett ställe, anpassat för dig. Synka mellan enheter och fortsätt där du slutade.",
    "landing.personalized_for_you": "anpassat för dig",
    "landing.start_radio": "Starta Radio",
    "landing.explore_podcasts": "Utforska Poddar",
    "landing.sign_in_full_experience": "Logga in för hela upplevelsen",
    "landing.why_streameater": "Varför Streameater?",
    "landing.features": "Funktioner",
    "landing.all_in_one": "Allt på ett ställe",
    "landing.all_in_one_desc":
      "Radio och poddar i samma app. Sluta byt mellan appar.",
    "landing.sync_everything": "Synka allt",
    "landing.sync_everything_desc":
      "Favoriter och historik följer med dig till alla dina enheter.",
    "landing.stable_reliable": "Stabilt & pålitligt",
    "landing.stable_reliable_desc":
      "Robusta strömmar med automatisk återanslutning.",
    "landing.curated_content": "Kuraterat innehåll",
    "landing.curated_content_desc":
      "Handplockade spellistor och mixar baserade på dina intressen.",
    "landing.smart_recommendations": "Smarta rekommendationer kommer snart",
    "landing.building_special": "Vi bygger något speciellt",
    "landing.get_started": "Kom igång på 30 sekunder",
    "landing.quickstart": "Snabbstart",
    "landing.step_1": "Välj Radio eller Poddar",
    "landing.step_2": "Bläddra eller sök efter det du gillar",
    "landing.step_3": "Tryck play – klart!",
    "landing.get_started_btn": "Kom igång",
    "landing.sign_in": "Logga in",
    "landing.popular_now": "Populärt just nu",
    "landing.top_charts": "Topplistan",
    "landing.local_favorites": "Lokala favoriter",
    "landing.worldwide_stations": "+ stationer världen över",
    "landing.play": "Spela",

    // FrontDoorFeatures
    "frontdoor.features.kicker": "Varför Streameater",
    "frontdoor.features.headline": "Allt på ett ställe.",
    "frontdoor.features.body":
      "Byt sömlöst mellan liveradio och dina favoritpoddar. All din lyssningshistorik och alla dina favoriter i en enhetlig upplevelse.",

    // Player
    "player.share": "Dela",
    "player.share_message_radio": "Jag lyssnar på {name} live på StreamEater. Lyssna här:",
    "player.share_message_podcast": "Jag lyssnar på {name} på StreamEater. Lyssna här:",
    "player.view_details": "Visa Detaljer",
    
    // Filter
    "filter.show_all": "Visa alla...",
    "filter.show_less": "Visa färre",
    "filter.search_country": "Sök land...",
  },
};

// Detect if user is likely Swedish based on browser language
const detectLanguage = (): Language => {
  // Check if user has manually set a language preference
  const stored = localStorage.getItem("se_language");
  if (stored === "sv" || stored === "en") {
    return stored;
  }

  // Auto-detect: if browser is Swedish, use Swedish
  const browserLang = navigator.language || navigator.languages?.[0] || "en";
  if (browserLang.toLowerCase().startsWith("sv")) {
    return "sv";
  }

  // Default to English for everyone else
  return "en";
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>(detectLanguage);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("se_language", lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const tCountry = (countryName: string): string => {
    // 1. Normalize/Shorten name first (e.g. "The United States Of America" -> "USA")
    const shortName = getDisplayCountryName(countryName);

    // 2. Translate if Swedish
    if (language === "sv") {
      return countryTranslations[shortName] || shortName; // Fallback to shortName
    }

    // 3. Return short name for English
    return shortName;
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage: handleSetLanguage, t, tCountry }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
